/**
 * 阅读增强功能生命周期模块
 */

export async function initEnhancedFeaturesLifecycle(context = {}) {
  const {
    state,
    elements,
    showToast,
    navigatePage,
    changeFontSize,
    changeTheme,
    initResizeHandler,
    bindIframeSelectionEvents,
    rerenderReaderForCurrentSize,
    loadReaderEnhancementsModule,
    uiVisibilityHandler: existingUiVisibilityHandler = null
  } = context;

  const enhancements = await loadReaderEnhancementsModule();
  const {
    initAutoScroll,
    initTTS,
    initReadingTimer,
    initTextSearch,
    initGestures,
    initImmersiveMode,
    initAutoNightMode
  } = enhancements;

  const autoScroll = initAutoScroll(state, elements, navigatePage, showToast);
  const tts = initTTS(state, elements, showToast);
  const readingTimer = initReadingTimer(state, showToast);
  const textSearch = initTextSearch(state, elements, showToast);

  const gestures = initGestures(elements, navigatePage, changeFontSize);
  gestures.init();

  const immersiveMode = initImmersiveMode(elements, state);
  immersiveMode.init();

  let uiVisibilityHandler = existingUiVisibilityHandler;
  if (elements?.readerOverlay && !uiVisibilityHandler) {
    uiVisibilityHandler = () => {
      // 仅 PDF 在工具栏显隐时需要重排，避免 EPUB/MOBI/AZW3 连续闪动。
      const fileType = state?.currentBook?.fileType;
      if (fileType !== 'pdf') {
        return;
      }
      requestAnimationFrame(() => {
        rerenderReaderForCurrentSize();
      });
    };
    elements.readerOverlay.addEventListener('ui-visibility-change', uiVisibilityHandler);
  }

  const autoNightMode = initAutoNightMode(state, changeTheme);
  autoNightMode.start();

  initResizeHandler();

  if (
    state?.currentBook?.fileType === 'epub' ||
    state?.currentBook?.fileType === 'mobi' ||
    state?.currentBook?.fileType === 'azw3'
  ) {
    setTimeout(() => bindIframeSelectionEvents(), 500);
    if (state?.currentBook?.fileType === 'epub' && state?.reader?.rendition) {
      state.reader.rendition.on('rendered', () => {
        setTimeout(() => bindIframeSelectionEvents(), 100);
        setTimeout(() => gestures?.rebindIframe?.(), 150);
      });
    }
  }

  if (elements?.autoScrollSpeedValue) {
    elements.autoScrollSpeedValue.textContent = `${state.readerSettings.autoScrollSpeed}秒/页`;
  }
  if (elements?.ttsRateValue) {
    elements.ttsRateValue.textContent = `${(Number(state.tts.rate) || 1).toFixed(1)}x`;
  }
  if (elements?.ttsPitchValue) {
    const pitch = Math.round(Number(state?.tts?.pitch) || 0);
    const sign = pitch >= 0 ? '+' : '';
    elements.ttsPitchValue.textContent = `${sign}${pitch}Hz`;
  }
  if (elements?.ttsVoiceSelect && state?.tts?.edgeVoice) {
    const targetVoice = String(state.tts.edgeVoice || '').trim();
    const hasTargetVoice = Array.from(elements.ttsVoiceSelect.options || []).some(option => option.value === targetVoice);
    if (hasTargetVoice) {
      elements.ttsVoiceSelect.value = targetVoice;
    }
  }
  if (elements?.timerStatus) {
    elements.timerStatus.textContent = '未设置定时';
  }

  if (state?.readerSettings?.dualPage && window.innerWidth >= 900) {
    elements?.dualPageBtn?.classList.add('active');
    elements?.readerOverlay?.classList.add('dual-page');
  }

  return {
    autoScroll,
    tts,
    readingTimer,
    textSearch,
    gestures,
    immersiveMode,
    autoNightMode,
    uiVisibilityHandler
  };
}

export function cleanupEnhancedFeaturesLifecycle(context = {}) {
  const {
    autoScroll,
    tts,
    readingTimer,
    textSearch,
    gestures,
    immersiveMode,
    autoNightMode,
    elements,
    uiVisibilityHandler,
    cleanupResizeHandler
  } = context;

  cleanupResizeHandler?.();

  autoScroll?.stop?.();
  tts?.stop?.();
  readingTimer?.stop?.();
  textSearch?.clear?.();
  gestures?.destroy?.();
  immersiveMode?.destroy?.();

  if (elements?.readerOverlay && uiVisibilityHandler) {
    elements.readerOverlay.removeEventListener('ui-visibility-change', uiVisibilityHandler);
  }

  autoNightMode?.stop?.();

  return {
    autoScroll: null,
    tts: null,
    readingTimer: null,
    textSearch: null,
    gestures: null,
    immersiveMode: null,
    autoNightMode: null,
    uiVisibilityHandler: null
  };
}
